import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.posthog.com';
const IS_PROD = import.meta.env.PROD;

let isInitialized = false;

/**
 * Returns true when analytics is enabled (a PostHog key is configured).
 * All tracking helpers short-circuit to a no-op when this is false, so
 * dev/test environments never send data unless explicitly configured.
 */
export function isAnalyticsEnabled(): boolean {
  return Boolean(POSTHOG_KEY);
}

/**
 * Initialize PostHog with POPIA-friendly defaults.
 * - EU region by default (via VITE_POSTHOG_HOST)
 * - Session recording disabled by default (opt-in later if needed)
 * - Respects Do-Not-Track
 * - Manual pageviews (captured from the Router)
 */
export function initAnalytics(): void {
  if (!POSTHOG_KEY) {
    if (IS_PROD) {
      // eslint-disable-next-line no-console
      console.warn('PostHog key not set - analytics disabled');
    }
    return;
  }

  if (isInitialized) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: false, // we capture pageviews manually on route change
    capture_pageleave: true,
    // POPIA-friendly defaults
    disable_session_recording: true,
    respect_dnt: true,
  });

  isInitialized = true;
}

export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  if (!POSTHOG_KEY) return;
  posthog.capture(event, properties);
}

export function identifyUser(userId: string, properties?: Record<string, unknown>): void {
  if (!POSTHOG_KEY) return;
  posthog.identify(userId, properties);
}

export function resetAnalytics(): void {
  if (!POSTHOG_KEY) return;
  posthog.reset();
}

export { posthog };
